import _some from 'lodash/some';
import { checkTokenExpiration } from '../utils/checkTokenExpiration';
import isUnderwriter from '../utils/is-underwriter'
import { FETCH_USERS, USER_ALERT_DISPLAY, FETCH_LOCKED_USERS } from './../constants/admin';
import * as adminActions from '../reducers/adminReducer';
import {
    USER_LOGGED_IN,
    USER_INVITE,
    USER_INVITE_RESEND,
    USER_INVITE_CANCEL
} from './../constants/user';

import {
    invite as adminInvite,
    deleteInvite as adminDeleteInvite,
    getCurrentSession
} from '../../lib/authentication'

import { fetch as fetchInvites } from './../invites/actions';
import { Alert } from '../alerts';
import * as alertActions from '../alerts/actions';

function userCanExecute(user, permission) {
    if (!user) return false;
    if (!user.permissions) return false;
    return _some([
        user.permissions.includes(permission),
        user.role.includes('admin') // legacy: backwards compatibility
    ]);
}

export function getUsersByBrokerage(user) {
    return dispatch => {
        const isUnderwriterFlag = isUnderwriter(user);
        return Promise.all([checkTokenExpiration(user), getCurrentSession()])
            .then((results) => {
                const [resp, currentSession] = results;
                let { idToken } = currentSession;
                if (resp.status === 'expired') {
                    dispatch({
                        type: USER_LOGGED_IN,
                        payload: resp.user,
                        meta: { from: 'getUsersByBrokerage' }
                    });
                }

                if (!userCanExecute(user, 'brokers:read')) {
                    // I guess this is a legitimate use of alert? maybe?
                    // eslint-disable-next-line
                    alert('You do not have required permissions to perform this action');
                    throw new Error('You do not have required permissions to perform this action');
                }

                let userTypeParam;
                if (isUnderwriterFlag) {
                    userTypeParam = { underWriter: true }
                } else {
                    userTypeParam = { broker: user.brokerId };
                }
                return apigClient.adminUsersGet(userTypeParam, {}, {
                    headers: {
                        Authorization: idToken
                    }
                });
            })
            .then((resp1) => {
                if (isUnderwriterFlag) {
                    const apiResponse = resp1.data; // why is this now data.data?
                    if (apiResponse.success) {
                        const underwriters = apiResponse.data.Items.filter(item =>
                            isUnderwriter(item)
                        );
                        dispatch({ type: FETCH_USERS, payload: underwriters });
                    } else {
                        // todo - talk about error handling
                        switch (apiResponse.errorCode) {
                            case 'NoParamError':
                                break;
                            case 'InternalError':
                                break;
                        }
                    }
                } else {
                    const apiResponse = resp1.data;
                    if (apiResponse.success) {
                        dispatch({ type: FETCH_USERS, payload: apiResponse.data });
                    } else {
                        // todo - talk about error handling
                        switch (apiResponse.errorCode) {
                            case 'NoParamError':
                                break;
                            case 'InternalError':
                                break;
                        }
                    }
                }
            })
            .catch((e) => {
                if (e instanceof Error) {
                    console.log('error', e.message);
                }
            });
    };
}

export function createNewUser(email, user, isAdmin, successMessage = `Success! An invite as been emailed to ${email}`) {
    return dispatch => {
        const { email: origin, role, brokerId } = user;
        let newRole = ''
        if (role.includes('underwriter')) {
            newRole = `underwriter${isAdmin === 'true' ? ':admin' : ''}`;
        } else {
            newRole = 'broker:admin'
        }
        const args = [email, origin, newRole];
        if (brokerId) {
            args.push(brokerId);
        }
        adminInvite(...args).then((result) => {
            const { success, error } = result;
            if (success) {
                dispatch(alertActions.setAlert(new Alert('success', successMessage, true)));
                dispatch(getUsersByBrokerage(user));
                dispatch(fetchInvites())
            }
        }).catch((error) => {
            dispatch(alertActions.setAlert(new Alert('danger', error.errorSummary, true)));
        })
    };
}

export function deleteUser(userToDelete, user, eventSource) {
    return dispatch => {
        checkTokenExpiration(user).then(resp => {
            if (!userCanExecute(user, 'brokers:write')) {
                alert('You do not have required permissions to perform this action'); // eslint-disable-line no-alert
                return;
            }

            const { id } = userToDelete;

            dispatch(adminDeleteInvite(id));

            // apigClient.adminUsersIdDelete({ id }, { id }, {}).then(resp1 => {
            //     if (!resp1.data.success) {
            //         dispatch(
            //             setAlert({
            //                 show: true,
            //                 message: `${resp1.data.message}`,
            //                 bsStyle: 'danger'
            //             })
            //         );
            //     } else {
            //         dispatch(inviteCancel(userToDelete, eventSource));
            //         dispatch(
            //             setAlert({
            //                 show: true,
            //                 message: 'Success: User was successfully removed.',
            //                 bsStyle: 'success'
            //             })
            //         );
            //     }

            dispatch(getUsersByBrokerage(user));
        });
    };
}

export function resendPasswordUser(userToResendTo, user, eventSource = null) {
    return dispatch => {
        checkTokenExpiration(user).then(resp => {
            if (resp.status === 'expired') {
                dispatch({ type: USER_LOGGED_IN, payload: resp.user, meta: { from: 'resendPasswordUser' } });
            }

            if (!userCanExecute(user, 'brokers:write')) {
                // I guess this is a legitimate use of alert? maybe?
                // eslint-disable-next-line
                alert('You do not have required permissions to perform this action');
                return;
            }

            apigClient
                .adminUsersIdDelete({ id: userToResendTo.id }, { id: userToResendTo.id }, {})
                .then(resp1 => {
                    if (resp1.success === false) {
                        dispatch(
                            alertActions.setAlert(new Alert('danger', `${resp1.data.message}`, true))
                        );
                    } else {
                        dispatch(inviteResend(userToResendTo.email));
                        dispatch(
                            createNewUser(
                                userToResendTo.email,
                                userToResendTo.role.includes('admin').toString(),
                                user,
                                eventSource,
                                'Success: User password has been resent.'
                            )
                        );
                    }
                });
        });
    };
}

export function updateUser(row, statusType, user) {
    return dispatch => {
        return Promise.all([checkTokenExpiration(user), getCurrentSession()]).then(results => {
            const [resp, currentSession] = results;
            const { idToken } = currentSession;
            if (resp.status === 'expired') {
                dispatch({ type: USER_LOGGED_IN, payload: resp.user, meta: { from: 'updateUser-1' } });
            }

            if (resp.status === 'expired') {
                dispatch({ type: USER_LOGGED_IN, payload: resp.user, meta: { from: 'updateUser-2' } });
            }

            return apigClient
                .adminUsersIdPut(
                    { id: row.id }, // id:
                    [{ fieldName: 'status', fieldValue: statusType }],
                    {
                        headers: {
                            Authorization: idToken
                        }
                    }
                );
        }).then((resp2, error2) => {
            if (
                resp2.data &&
                resp2.data.success === true &&
                (statusType === 'disabled' || statusType === 'revoked')
            ) {
                dispatch(
                    alertActions.setAlert(new Alert('warning', `Account ${statusType} for: ${
                        row.username
                        }. You will still be able to access their submissions.`, true)));
                dispatch(getUsersByBrokerage(user));
            } else if (
                resp2.data &&
                resp2.data.success === true &&
                statusType !== 'disabled' &&
                statusType !== 'revoked'
            ) {
                const message = () => {
                    switch (statusType) {
                        case 'active':
                            return `Account reactivated for: ${
                                row.username
                                }. They can log in with their existing credentials.`;
                        case 'disabled':
                            return `Account disabled for: ${
                                row.username
                                }. You will still be able to access their submissions.`;
                        case 'revoked':
                            return `Account revoked for: ${
                                row.username
                                }. You will still be able to access their submissions.`;
                        default:
                            return 'You have successefully made an update!';
                    }
                };
                dispatch(alertActions.setAlert(new Alert('success', message(), true)));
                dispatch(getUsersByBrokerage(user));
            } else if (resp2.data && resp2.data.success === false) {
                dispatch(
                    alertActions.setAlert(new Alert('danger', `${
                        resp2.data.message
                        }. Please contact your administrator for further support`, true))
                );
            } else {
                dispatch(
                    alertActions.setAlert(new Alert('danger', 'There was an error processing your request. Please contact your administrator.', true))
                );
            }
        });
    };
}


export function invite(email, role, referrerUserType = null, eventSource = null) {
    return {
        type: USER_INVITE,
        value: {
            email,
            role,
            referrerUserType,
            eventSource
        }
    };
}

export function inviteResend(email) {
    return {
        type: USER_INVITE_RESEND,
        value: email
    };
}

export function inviteCancel(user, eventSource = null) {
    return {
        type: USER_INVITE_CANCEL,
        value: {
            user,
            eventSource
        }
    };
}

export function getLockedUsers() {
    return dispatch => {
        dispatch(adminActions({ type: FETCH_LOCKED_USERS, payload: {} }));
    }
}

export function setAlert(params) {
    return dispatch => {
        dispatch({ type: USER_ALERT_DISPLAY, payload: params });
    };
}
